import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import LangSwitch from "@/components/LangSwitch";
import ThemeSwitch from "@/components/ThemeSwitch";
import { Phone, ArrowLeft } from "lucide-react";
import Marque from "../components/Marque";

// Coordonnées communes (identiques à Tarifs.js).
const CONTACT = "quotes@roofborder.com";
// Numéro sans frais : « vanity » à l'affichage, chiffres réels dans le lien tel:.
const TEL_AFFICHE = "1-866-4ROOF-21";
const TEL_LIEN = "tel:+18664766321";

// Entité exploitante et juridiction (fournies par Jeff le 2026-08-01).
const ENTITE = "Roof Border, 506 rue de la Chapelle, Deux-Montagnes, Québec, Canada";
const JURIDICTION_EN = "Province of Quebec, Canada";
const JURIDICTION_FR = "Province de Québec, Canada";
const JURIDICTION_ES = "Provincia de Quebec, Canadá";

// Tout le texte de la page dans les trois langues (même patron que Tarifs.js).
// Les sections sont des objets { titre, corps } ; « corps » est un tableau
// de blocs : chaîne = paragraphe, tableau = liste à puces.
const T = {
  en: {
    retour: "Back to home",
    titre: "Terms of Use",
    maj: "Last updated: August 1, 2026",
    intro:
      "These Terms of Use (the \"Terms\") govern your access to and use of Roof Border. By creating an account or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.",
    avertTitre: "Estimates are not firm quotes",
    avertCorps:
      "Measurements and prices produced by Roof Border are approximate ESTIMATES provided for guidance only. They are not a firm quote, an offer or a contract. The Roofer remains solely responsible for verifying every measurement and for setting the final price. Roof Border is not a party to any contract between the Roofer and their client.",
    modeleNote:
      "This document is a template. Have it reviewed by legal counsel before final publication.",
    sections: [
      {
        titre: "1. Acceptance of the Terms",
        corps: [
          "By registering for, accessing or using Roof Border, you confirm that you have read, understood and accepted these Terms, together with our Privacy Policy. If you use the Service on behalf of a business, you represent that you have authority to bind that business to these Terms.",
          "Definitions used throughout this document:",
          [
            "\"Service\" means the Roof Border platform, website, estimate widget, lead tracking, AI receptionist and any related software or services.",
            "\"User\" or \"Roofer\" means the roofing professional or company that holds an account and uses the Service.",
            "\"Prospect\" means an end customer who requests an estimate through the Roofer, including via the widget.",
            "\"Quote\" means a document generated by the Service from measurements and from the prices entered by the Roofer.",
          ],
        ],
      },
      {
        titre: "2. Description of the Service",
        corps: [
          "Roof Border measures roofs from satellite imagery using artificial intelligence and produces quotes based on the prices entered by the Roofer. From a simple address, the system estimates roof surfaces and pitch and applies the Roofer's pricing to generate a branded quote.",
          "Depending on the plan chosen, the Service may also include an embeddable estimate widget for the Roofer's website, prospect tracking, an AI receptionist that answers calls, and optional custom websites. Features may evolve over time.",
        ],
      },
      {
        titre: "3. Accounts",
        corps: [
          "You must provide accurate, current and complete information when creating your account and keep it up to date. You are responsible for maintaining the confidentiality of your password and for all activity that occurs under your account.",
          "You must be legally able to enter into a binding contract to open an account. Only one account is permitted per business. You must notify us promptly of any unauthorized use of your account.",
        ],
      },
      {
        titre: "4. Subscriptions and payment",
        corps: [
          "The Service is offered through monthly or annual subscription plans billed securely through Stripe. Subscriptions renew automatically at the end of each billing period unless cancelled beforehand.",
          "Prices are charged in Canadian dollars or US dollars depending on your country. Usage above the volume included in your plan is billed at the overage rate stated for that plan. Applicable taxes are added.",
          "You may cancel at any time; cancellation stops the next renewal. Except where expressly stated, payments already made are non-refundable and are not pro-rated. You remain responsible for any amounts accrued before cancellation.",
        ],
      },
      {
        titre: "5. Referral program",
        corps: [
          "When you refer another Roofer who subscribes, you may earn referral credits equal to 10% of that referred customer's plan. Referral credits can only be spent within the Service; they are not redeemable for cash and have no monetary value outside the Service.",
          "Credits stop accruing if the referred customer cancels. Roof Border may modify, suspend or end the referral program, or adjust credit rules, at any time.",
        ],
      },
      {
        titre: "6. Nature of estimates",
        corps: [
          "This point is essential. All measurements and prices produced by the Service are approximate ESTIMATES intended for guidance only. They do not constitute a firm quote, a binding offer or a contract.",
          "The Roofer remains solely responsible for verifying every measurement on site and for establishing the final price charged to the Prospect. Roof Border does not guarantee the accuracy of satellite measurements and is not a party to any agreement between the Roofer and their client.",
        ],
      },
      {
        titre: "7. Acceptable use",
        corps: [
          "You agree not to misuse the Service. In particular, you must not:",
          [
            "reverse engineer, decompile or attempt to derive the source code of the Service;",
            "overload, probe or disrupt the Service or its APIs, or exceed reasonable request limits;",
            "use the estimate widget to collect personal data without the individual's consent;",
            "use the Service for any unlawful purpose or in violation of any applicable law.",
          ],
          "You are responsible for the follow-up emails and messages you send through the Service and must comply with anti-spam laws, including Canada's Anti-Spam Legislation (CASL) and the United States CAN-SPAM Act. Roof Border is not responsible for communications you choose to send.",
        ],
      },
      {
        titre: "8. Intellectual property",
        corps: [
          "Roof Border and its licensors retain all rights, title and interest in the Service, including its software, design and trademarks. We grant you a limited, non-exclusive, non-transferable license to use the Service in accordance with these Terms.",
          "You retain all rights to the data and content you provide, including your prices, your clients' information and your branding. You grant us the limited license needed to host and process that data in order to operate the Service for you.",
        ],
      },
      {
        titre: "9. Availability, warranties and liability",
        corps: [
          "The Service is provided \"as is\" and \"as available\", without warranty of any kind, whether express or implied, including fitness for a particular purpose. We do not warrant that the Service will be uninterrupted, error-free or that measurements will be accurate.",
          "To the maximum extent permitted by law, Roof Border's total liability arising out of or relating to the Service is limited to the amounts you paid to us in the twelve (12) months preceding the event giving rise to the claim.",
          "You agree to indemnify and hold harmless Roof Border and " + ENTITE + " from any claim, loss or expense arising from your use of the Service, your content, or your breach of these Terms.",
        ],
      },
      {
        titre: "10. Termination",
        corps: [
          "You may terminate your account at any time by cancelling your subscription. We may suspend or terminate your access if you breach these Terms or use the Service in a way that harms Roof Border, its users or third parties.",
          "On termination, your right to use the Service ends. Provisions that by their nature should survive termination — including intellectual property, disclaimers, limitation of liability and indemnification — will continue to apply.",
        ],
      },
      {
        titre: "11. Changes, governing law and jurisdiction",
        corps: [
          "We may update these Terms from time to time. Material changes will be communicated by reasonable means; continued use of the Service after the changes take effect constitutes acceptance.",
          "These Terms are governed by the laws applicable in " + JURIDICTION_EN + ", and any dispute will be submitted to the competent courts of that jurisdiction. The legal entity operating the Service is " + ENTITE + ".",
        ],
      },
      {
        titre: "12. Contact",
        corps: [
          "For any question about these Terms, contact us:",
          [
            "Email: " + CONTACT,
            "Phone: " + TEL_AFFICHE,
          ],
        ],
      },
    ],
  },
  fr: {
    retour: "Retour à l'accueil",
    titre: "Conditions d'utilisation",
    maj: "Dernière mise à jour : 1 août 2026",
    intro:
      "Les présentes conditions d'utilisation (les « Conditions ») régissent votre accès à Roof Border et votre utilisation du Service. En créant un compte ou en utilisant le Service, vous acceptez d'être lié par les présentes Conditions. Si vous n'êtes pas d'accord, n'utilisez pas le Service.",
    avertTitre: "Les estimations ne sont pas des soumissions fermes",
    avertCorps:
      "Les mesures et les prix produits par Roof Border sont des ESTIMATIONS approximatives fournies à titre indicatif seulement. Ils ne constituent pas une soumission ferme, une offre ni un contrat. Le Couvreur demeure seul responsable de vérifier chaque mesure et d'établir le prix final. Roof Border n'est pas partie au contrat entre le Couvreur et son client.",
    modeleNote:
      "Ce document est un modèle. Faites-le réviser par un conseiller juridique avant la mise en ligne définitive.",
    sections: [
      {
        titre: "1. Acceptation des conditions",
        corps: [
          "En vous inscrivant, en accédant à Roof Border ou en l'utilisant, vous confirmez avoir lu, compris et accepté les présentes Conditions, ainsi que notre politique de confidentialité. Si vous utilisez le Service pour le compte d'une entreprise, vous déclarez avoir le pouvoir de lier cette entreprise aux présentes Conditions.",
          "Définitions employées dans le présent document :",
          [
            "« Service » désigne la plateforme Roof Border, le site web, le widget d'estimation, le suivi de prospects, la réceptionniste IA et tout logiciel ou service connexe.",
            "« Utilisateur » ou « Couvreur » désigne le professionnel ou l'entreprise de couverture qui détient un compte et utilise le Service.",
            "« Prospect » désigne le client final qui demande une estimation par l'entremise du Couvreur, notamment via le widget.",
            "« Soumission » désigne un document généré par le Service à partir des mesures et des prix saisis par le Couvreur.",
          ],
        ],
      },
      {
        titre: "2. Description du Service",
        corps: [
          "Roof Border mesure les toits par imagerie satellite et intelligence artificielle et produit des soumissions à partir des prix saisis par le Couvreur. À partir d'une simple adresse, le système estime les surfaces et la pente du toit, puis applique la tarification du Couvreur pour générer une soumission à son image.",
          "Selon le forfait choisi, le Service peut aussi comprendre un widget d'estimation intégrable au site web du Couvreur, le suivi de prospects, une réceptionniste IA qui répond aux appels et, en option, des sites web sur mesure. Les fonctionnalités peuvent évoluer avec le temps.",
        ],
      },
      {
        titre: "3. Comptes",
        corps: [
          "Vous devez fournir des renseignements exacts, à jour et complets lors de la création de votre compte et les tenir à jour. Vous êtes responsable de la confidentialité de votre mot de passe et de toute activité effectuée sous votre compte.",
          "Vous devez être légalement en mesure de conclure un contrat pour ouvrir un compte. Un seul compte est autorisé par entreprise. Vous devez nous aviser sans délai de toute utilisation non autorisée de votre compte.",
        ],
      },
      {
        titre: "4. Abonnements et paiement",
        corps: [
          "Le Service est offert par forfaits d'abonnement mensuels ou annuels, facturés de façon sécurisée par Stripe. Les abonnements se renouvellent automatiquement à la fin de chaque période de facturation, à moins d'être annulés au préalable.",
          "Les prix sont facturés en dollars canadiens ou en dollars américains selon votre pays. L'utilisation qui dépasse le volume inclus dans votre forfait est facturée au tarif de surplus indiqué pour ce forfait. Les taxes applicables sont en sus.",
          "Vous pouvez annuler en tout temps ; l'annulation met fin au prochain renouvellement. Sauf mention expresse, les paiements déjà effectués ne sont pas remboursables et ne sont pas calculés au prorata. Vous demeurez responsable des sommes accumulées avant l'annulation.",
        ],
      },
      {
        titre: "5. Programme de parrainage",
        corps: [
          "Lorsque vous parrainez un autre Couvreur qui s'abonne, vous pouvez obtenir des crédits de parrainage équivalant à 10 % du forfait du filleul. Les crédits de parrainage ne peuvent être dépensés qu'à l'intérieur du Service ; ils ne sont pas échangeables contre de l'argent et n'ont aucune valeur monétaire hors du Service.",
          "Les crédits cessent de s'accumuler si le filleul annule. Roof Border peut modifier, suspendre ou mettre fin au programme de parrainage, ou en ajuster les règles, en tout temps.",
        ],
      },
      {
        titre: "6. Nature des estimations",
        corps: [
          "Ce point est essentiel. Toutes les mesures et tous les prix produits par le Service sont des ESTIMATIONS approximatives fournies à titre indicatif seulement. Ils ne constituent pas une soumission ferme, une offre ferme ni un contrat.",
          "Le Couvreur demeure seul responsable de vérifier chaque mesure sur place et d'établir le prix final facturé au Prospect. Roof Border ne garantit pas l'exactitude des mesures satellite et n'est pas partie à l'entente conclue entre le Couvreur et son client.",
        ],
      },
      {
        titre: "7. Utilisation acceptable",
        corps: [
          "Vous vous engagez à ne pas abuser du Service. En particulier, vous ne devez pas :",
          [
            "rétroconcevoir, décompiler ou tenter d'extraire le code source du Service ;",
            "surcharger, sonder ou perturber le Service ou ses API, ni dépasser des limites de requêtes raisonnables ;",
            "utiliser le widget d'estimation pour collecter des données personnelles sans le consentement de la personne concernée ;",
            "utiliser le Service à des fins illégales ou en violation d'une loi applicable.",
          ],
          "Vous êtes responsable des courriels et messages de suivi que vous envoyez au moyen du Service et devez respecter les lois anti-pourriel, notamment la Loi canadienne anti-pourriel (LCAP) et la loi CAN-SPAM des États-Unis. Roof Border n'est pas responsable des communications que vous choisissez d'envoyer.",
        ],
      },
      {
        titre: "8. Propriété intellectuelle",
        corps: [
          "Roof Border et ses concédants conservent l'ensemble des droits, titres et intérêts sur le Service, y compris son logiciel, son design et ses marques de commerce. Nous vous accordons une licence limitée, non exclusive et non transférable d'utilisation du Service conformément aux présentes Conditions.",
          "Vous conservez tous les droits sur les données et le contenu que vous fournissez, notamment vos prix, les renseignements de vos clients et votre image de marque. Vous nous accordez la licence limitée nécessaire pour héberger et traiter ces données afin d'exploiter le Service pour vous.",
        ],
      },
      {
        titre: "9. Disponibilité, garanties et responsabilité",
        corps: [
          "Le Service est fourni « tel quel » et « selon sa disponibilité », sans garantie d'aucune sorte, expresse ou implicite, y compris toute garantie d'adéquation à un usage particulier. Nous ne garantissons pas que le Service sera ininterrompu, exempt d'erreurs, ni que les mesures seront exactes.",
          "Dans toute la mesure permise par la loi, la responsabilité totale de Roof Border découlant du Service ou s'y rapportant est limitée aux sommes que vous nous avez versées au cours des douze (12) derniers mois précédant l'événement à l'origine de la réclamation.",
          "Vous acceptez d'indemniser et de dégager de toute responsabilité Roof Border et " + ENTITE + " à l'égard de toute réclamation, perte ou dépense découlant de votre utilisation du Service, de votre contenu ou de votre manquement aux présentes Conditions.",
        ],
      },
      {
        titre: "10. Résiliation",
        corps: [
          "Vous pouvez résilier votre compte en tout temps en annulant votre abonnement. Nous pouvons suspendre ou résilier votre accès si vous enfreignez les présentes Conditions ou utilisez le Service d'une manière qui nuit à Roof Border, à ses utilisateurs ou à des tiers.",
          "À la résiliation, votre droit d'utiliser le Service prend fin. Les dispositions qui, par leur nature, doivent survivre à la résiliation — notamment la propriété intellectuelle, les exonérations, la limitation de responsabilité et l'indemnisation — continuent de s'appliquer.",
        ],
      },
      {
        titre: "11. Modifications, droit applicable et tribunaux compétents",
        corps: [
          "Nous pouvons mettre à jour les présentes Conditions de temps à autre. Les changements importants seront communiqués par des moyens raisonnables ; l'utilisation continue du Service après leur entrée en vigueur vaut acceptation.",
          "Les présentes Conditions sont régies par les lois applicables au " + JURIDICTION_FR + ", et tout litige sera soumis aux tribunaux compétents de cette juridiction. L'entité légale exploitant le Service est " + ENTITE + ".",
        ],
      },
      {
        titre: "12. Coordonnées",
        corps: [
          "Pour toute question au sujet des présentes Conditions, communiquez avec nous :",
          [
            "Courriel : " + CONTACT,
            "Téléphone : " + TEL_AFFICHE,
          ],
        ],
      },
    ],
  },
  es: {
    retour: "Volver al inicio",
    titre: "Términos de uso",
    maj: "Última actualización: 1 de agosto de 2026",
    intro:
      "Los presentes términos de uso (los «Términos») rigen su acceso a Roof Border y su uso del Servicio. Al crear una cuenta o utilizar el Servicio, usted acepta quedar obligado por estos Términos. Si no está de acuerdo, no utilice el Servicio.",
    avertTitre: "Los estimados no son cotizaciones en firme",
    avertCorps:
      "Las mediciones y los precios producidos por Roof Border son ESTIMADOS aproximados proporcionados únicamente a título indicativo. No constituyen una cotización en firme, una oferta ni un contrato. El Techador sigue siendo el único responsable de verificar cada medición y de establecer el precio final. Roof Border no es parte del contrato entre el Techador y su cliente.",
    modeleNote:
      "Este documento es un modelo. Hágalo revisar por un asesor jurídico antes de su publicación definitiva.",
    sections: [
      {
        titre: "1. Aceptación de los términos",
        corps: [
          "Al registrarse, acceder a Roof Border o utilizarlo, usted confirma que ha leído, comprendido y aceptado los presentes Términos, así como nuestra política de privacidad. Si utiliza el Servicio en nombre de una empresa, usted declara tener la facultad de obligar a dicha empresa a estos Términos.",
          "Definiciones empleadas en el presente documento:",
          [
            "«Servicio» designa la plataforma Roof Border, el sitio web, el widget de estimación, el seguimiento de prospectos, la recepcionista con IA y todo software o servicio relacionado.",
            "«Usuario» o «Techador» designa al profesional o la empresa de techado que posee una cuenta y utiliza el Servicio.",
            "«Prospecto» designa al cliente final que solicita un estimado por medio del Techador, en particular a través del widget.",
            "«Cotización» designa un documento generado por el Servicio a partir de las mediciones y de los precios ingresados por el Techador.",
          ],
        ],
      },
      {
        titre: "2. Descripción del Servicio",
        corps: [
          "Roof Border mide los techos mediante imágenes satelitales e inteligencia artificial y produce cotizaciones a partir de los precios ingresados por el Techador. A partir de una simple dirección, el sistema estima las superficies y la pendiente del techo y aplica la tarificación del Techador para generar una cotización con su imagen.",
          "Según el plan elegido, el Servicio también puede incluir un widget de estimación integrable al sitio web del Techador, el seguimiento de prospectos, una recepcionista con IA que contesta las llamadas y, de forma opcional, sitios web a la medida. Las funciones pueden evolucionar con el tiempo.",
        ],
      },
      {
        titre: "3. Cuentas",
        corps: [
          "Usted debe proporcionar información exacta, actual y completa al crear su cuenta y mantenerla actualizada. Usted es responsable de la confidencialidad de su contraseña y de toda actividad realizada bajo su cuenta.",
          "Usted debe tener capacidad legal para celebrar un contrato para abrir una cuenta. Se permite una sola cuenta por empresa. Debe notificarnos sin demora cualquier uso no autorizado de su cuenta.",
        ],
      },
      {
        titre: "4. Suscripciones y pago",
        corps: [
          "El Servicio se ofrece mediante planes de suscripción mensuales o anuales, facturados de forma segura a través de Stripe. Las suscripciones se renuevan automáticamente al final de cada período de facturación, salvo que se cancelen con antelación.",
          "Los precios se cobran en dólares canadienses o en dólares estadounidenses según su país. El uso que supere el volumen incluido en su plan se factura a la tarifa de excedente indicada para ese plan. Los impuestos aplicables se suman aparte.",
          "Usted puede cancelar en cualquier momento; la cancelación detiene la siguiente renovación. Salvo indicación expresa, los pagos ya efectuados no son reembolsables ni se prorratean. Usted sigue siendo responsable de los montos acumulados antes de la cancelación.",
        ],
      },
      {
        titre: "5. Programa de referidos",
        corps: [
          "Cuando usted refiere a otro Techador que se suscribe, puede obtener créditos de referido equivalentes al 10 % del plan del referido. Los créditos de referido solo pueden utilizarse dentro del Servicio; no son canjeables por dinero y no tienen valor monetario fuera del Servicio.",
          "Los créditos dejan de acumularse si el referido cancela. Roof Border puede modificar, suspender o poner fin al programa de referidos, o ajustar sus reglas, en cualquier momento.",
        ],
      },
      {
        titre: "6. Naturaleza de los estimados",
        corps: [
          "Este punto es esencial. Todas las mediciones y todos los precios producidos por el Servicio son ESTIMADOS aproximados proporcionados únicamente a título indicativo. No constituyen una cotización en firme, una oferta en firme ni un contrato.",
          "El Techador sigue siendo el único responsable de verificar cada medición en el sitio y de establecer el precio final cobrado al Prospecto. Roof Border no garantiza la exactitud de las mediciones satelitales y no es parte del acuerdo celebrado entre el Techador y su cliente.",
        ],
      },
      {
        titre: "7. Uso aceptable",
        corps: [
          "Usted se compromete a no abusar del Servicio. En particular, no debe:",
          [
            "aplicar ingeniería inversa, descompilar o intentar extraer el código fuente del Servicio;",
            "sobrecargar, sondear o perturbar el Servicio o sus API, ni superar límites de solicitudes razonables;",
            "utilizar el widget de estimación para recopilar datos personales sin el consentimiento de la persona interesada;",
            "utilizar el Servicio con fines ilícitos o en violación de cualquier ley aplicable.",
          ],
          "Usted es responsable de los correos y mensajes de seguimiento que envíe a través del Servicio y debe cumplir con las leyes antispam, en particular la Ley canadiense antispam (CASL) y la ley CAN-SPAM de los Estados Unidos. Roof Border no es responsable de las comunicaciones que usted decida enviar.",
        ],
      },
      {
        titre: "8. Propiedad intelectual",
        corps: [
          "Roof Border y sus licenciantes conservan todos los derechos, títulos e intereses sobre el Servicio, incluidos su software, su diseño y sus marcas. Le otorgamos una licencia limitada, no exclusiva e intransferible para utilizar el Servicio de conformidad con los presentes Términos.",
          "Usted conserva todos los derechos sobre los datos y el contenido que proporciona, en particular sus precios, la información de sus clientes y su imagen de marca. Usted nos otorga la licencia limitada necesaria para alojar y procesar esos datos con el fin de operar el Servicio para usted.",
        ],
      },
      {
        titre: "9. Disponibilidad, garantías y responsabilidad",
        corps: [
          "El Servicio se proporciona «tal cual» y «según disponibilidad», sin garantía de ningún tipo, expresa o implícita, incluida cualquier garantía de idoneidad para un fin determinado. No garantizamos que el Servicio sea ininterrumpido, esté libre de errores ni que las mediciones sean exactas.",
          "En la máxima medida permitida por la ley, la responsabilidad total de Roof Border derivada del Servicio o relacionada con él se limita a los montos que usted nos haya pagado durante los doce (12) meses anteriores al hecho que origina la reclamación.",
          "Usted acepta indemnizar y eximir de responsabilidad a Roof Border y a " + ENTITE + " respecto de cualquier reclamación, pérdida o gasto derivado de su uso del Servicio, de su contenido o de su incumplimiento de los presentes Términos.",
        ],
      },
      {
        titre: "10. Terminación",
        corps: [
          "Usted puede dar por terminada su cuenta en cualquier momento cancelando su suscripción. Nosotros podemos suspender o dar por terminado su acceso si usted incumple los presentes Términos o utiliza el Servicio de una manera que perjudique a Roof Border, a sus usuarios o a terceros.",
          "Al producirse la terminación, su derecho a utilizar el Servicio finaliza. Las disposiciones que, por su naturaleza, deban subsistir tras la terminación — en particular la propiedad intelectual, las exenciones, la limitación de responsabilidad y la indemnización — seguirán aplicándose.",
        ],
      },
      {
        titre: "11. Modificaciones, ley aplicable y tribunales competentes",
        corps: [
          "Podemos actualizar los presentes Términos de vez en cuando. Los cambios importantes se comunicarán por medios razonables; el uso continuado del Servicio después de su entrada en vigor constituye aceptación.",
          "Los presentes Términos se rigen por las leyes aplicables en " + JURIDICTION_ES + ", y todo litigio se someterá a los tribunales competentes de dicha jurisdicción. La entidad legal que opera el Servicio es " + ENTITE + ".",
        ],
      },
      {
        titre: "12. Contacto",
        corps: [
          "Para cualquier pregunta sobre los presentes Términos, comuníquese con nosotros:",
          [
            "Correo: " + CONTACT,
            "Teléfono: " + TEL_AFFICHE,
          ],
        ],
      },
    ],
  },
};

export default function Conditions() {
  const { lang } = useI18n();
  const L = T[lang] || T.en;

  // Rendu d'un bloc de section : chaîne = paragraphe, tableau = liste à puces.
  const rendreBloc = (bloc, i) =>
    Array.isArray(bloc) ? (
      <ul key={i} className="list-disc pl-6 space-y-2 text-ink-soft leading-relaxed">
        {bloc.map((li, j) => (
          <li key={j}>{li}</li>
        ))}
      </ul>
    ) : (
      <p key={i} className="text-ink-soft leading-relaxed">
        {bloc}
      </p>
    );

  return (
    <div className="min-h-screen bg-app text-ink">
      {/* entête (même patron que Tarifs.js) */}
      <header className="border-b border-line">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Marque taille="md" />
          <div className="flex items-center gap-4">
            <a href={TEL_LIEN} className="hidden md:inline-flex items-center gap-1.5 text-sm font-bold text-brand-ink hover:text-brand transition-colors">
              <Phone className="h-4 w-4" /> {TEL_AFFICHE}
            </a>
            <ThemeSwitch />
            <LangSwitch langues={["en", "fr", "es"]} />
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-soft hover:text-ink transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> {L.retour}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14">
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter mb-3">
          {L.titre}
        </h1>
        <p className="text-sm text-ink-faint mb-8">{L.maj}</p>

        {/* Note « modèle » bien visible, en italique. */}
        <div className="rounded-lg border border-line-strong bg-field px-5 py-4 mb-8">
          <p className="text-sm italic text-ink-soft">{L.modeleNote}</p>
        </div>

        <p className="text-ink-soft leading-relaxed mb-10">{L.intro}</p>

        {/* Avertissement mis en évidence sur les estimations. */}
        <div className="rounded-xl border border-orange-500/50 bg-orange-500/[0.07] px-6 py-5 mb-12">
          <h2 className="font-heading font-bold text-lg mb-2 text-brand-ink">
            {L.avertTitre}
          </h2>
          <p className="text-ink-soft leading-relaxed">{L.avertCorps}</p>
        </div>

        {/* Sections en prose. */}
        <div className="space-y-10">
          {L.sections.map((s, i) => (
            <section key={i} className="space-y-3">
              <h2 className="font-heading font-bold text-2xl tracking-tight">{s.titre}</h2>
              {s.corps.map((bloc, j) => rendreBloc(bloc, j))}
              {s.note && <p className="text-sm italic text-ink-faint">{s.note}</p>}
            </section>
          ))}
        </div>
      </main>

      {/* pied de page (identique à Tarifs.js) */}
      <footer className="border-t border-line mt-16">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Marque taille="md" />
          <div className="flex items-center gap-6 text-sm text-ink-soft">
            <a href={`mailto:${CONTACT}`} className="hover:text-ink transition-colors">
              {CONTACT}
            </a>
            <a href={TEL_LIEN} className="inline-flex items-center gap-1.5 hover:text-ink transition-colors">
              <Phone className="h-4 w-4" /> {TEL_AFFICHE}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
